// ---------------------------------------------------------------------------
// SCHEMA + AEO HELPERS — one place that builds every JSON-LD graph and every
// generated FAQ on the site. Pages import from here; never inline schema.
//
// SECURITY: schema is built ONLY from public catalog data (trade names, city
// names, county names). It never touches a listing's city, ZIP, address,
// contact block, notes or document paths.
// ---------------------------------------------------------------------------

import { BRAND, LEAD_PRICE, PLAN_INCLUDED_LEADS, PLAN_PRICES, money } from '@/data/constants';
import { Lang } from '@/data/constants';
import { brandCase, BRAND_DOMAIN_LOWER } from '@/lib/brand';

export interface Faq { q: string; a: string }

export const origin = () =>
  typeof window === 'undefined' ? `https://${BRAND_DOMAIN_LOWER}` : window.location.origin;


export const abs = (path: string) => `${origin()}${path.startsWith('/') ? path : `/${path}`}`;

/** BreadcrumbList — every programmatic page must render one. */
export const breadcrumbSchema = (items: { name: string; url: string }[]) => ({
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: abs(it.url),
  })),
});

/** FAQPage — powers AI Overviews, Perplexity answers and rich results. */
export const faqSchema = (faqs: Faq[]) => ({
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

/**
 * WEBSITE SCHEMA WAS REMOVED FROM HERE — do not re-add it to a page.
 *
 * Two defects were fixed by deleting this helper:
 *
 * 1. FALSE SearchAction. It advertised a sitebox at
 *    `/{lang}/jobs?q={search_term_string}` while src/pages/Jobs.tsx read only
 *    `trade`, `state`, `type`, `budget` and `city`, so a `?q=` URL returned the
 *    unfiltered feed. A real keyword search now EXISTS on that page (`q` is
 *    read from the URL, rendered in a search input and applied to the public
 *    fields in KEYWORD_SEARCH_FIELDS), so the SearchAction has been re-added —
 *    but on the single canonical WebSite node inside <Seo>, NOT here.
 *
 * 2. WRONG PLACEMENT. It was attached to only two pages (About and the Leads
 *    hub) and never to the homepage, while the site-name / site-structure
 *    signal belongs on every page.
 *
 * The WebSite entity (with its SearchAction) is emitted once per page by <Seo>
 * with a stable `@id` of `${origin}/#website`, alongside Organization and a
 * page-specific WebPage node. Adding another WebSite node here would duplicate
 * that entity AND duplicate the SearchAction.
 */



/** Service entity for a trade in a place (GEO entity relationship). */
export const serviceSchema = (opts: { trade: string; area: string; description: string; url: string }) => ({
  '@type': 'Service',
  serviceType: opts.trade,
  provider: { '@type': 'Organization', name: BRAND.fullName, url: origin() },
  areaServed: { '@type': 'AdministrativeArea', name: opts.area },
  description: opts.description,
  url: abs(opts.url),
});

/** Local marketplace presence for a city or county page. */
export const localBusinessSchema = (opts: { name: string; area: string; url: string; region?: string }) => ({
  '@type': 'LocalBusiness',
  '@id': abs(opts.url),
  name: `${BRAND.fullName} — ${opts.name}`,
  url: abs(opts.url),
  email: BRAND.email,
  priceRange: '$$',
  areaServed: { '@type': 'AdministrativeArea', name: opts.area },
  address: { '@type': 'PostalAddress', addressRegion: opts.region || 'CA', addressCountry: 'US' },
});

export const articleSchema = (opts: { headline: string; description: string; url: string; topic: string }) => ({
  '@type': 'Article',
  headline: opts.headline,
  description: opts.description,
  articleSection: opts.topic,
  url: abs(opts.url),
  author: { '@type': 'Organization', name: BRAND.fullName },
  publisher: { '@type': 'Organization', name: BRAND.fullName },
  inLanguage: 'en',
});

/** ItemList used by directory / hub pages so crawlers see the full inventory. */
export const itemListSchema = (name: string, items: { name: string; url: string }[]) => ({
  '@type': 'ItemList',
  name,
  numberOfItems: items.length,
  itemListElement: items.slice(0, 100).map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    url: abs(it.url),
  })),
});

/** Offer schema for membership / lead pricing pages. */
export const offerCatalogSchema = () => ({
  '@type': 'OfferCatalog',
  name: `${BRAND.fullName} Memberships`,
  itemListElement: Object.keys(PLAN_PRICES).map((code) => ({
    '@type': 'Offer',
    name: `${code.charAt(0).toUpperCase()}${code.slice(1)} membership`,
    price: (PLAN_PRICES[code] / 100).toFixed(2),
    priceCurrency: 'USD',
    description: `${PLAN_INCLUDED_LEADS[code]} included construction leads per month.`,
  })),
});

// ---------------------------------------------------------------------------
// AEO FAQ GENERATORS — bilingual, question-first, snippet-length answers.
//
// BRAND: every generated question/answer leaves through `fx()`, so the wordmark
// is guaranteed to render as GIGGONE both in the visible FAQ block and in the
// FAQPage JSON-LD consumed by AI Overviews / Perplexity / Copilot.
// ---------------------------------------------------------------------------

const LEAD = money(LEAD_PRICE);

/** Normalise the wordmark across a generated FAQ list. */
const fx = (list: Faq[]): Faq[] => list.map((f) => ({ q: brandCase(f.q), a: brandCase(f.a) }));

export const platformFaqs = (lang: Lang): Faq[] =>
  fx(
    lang === 'es'
      ? [
          { q: '¿Cómo funciona GIGGONE?', a: `Los propietarios y contratistas generales publican proyectos reales. Los subcontratistas ven el oficio, el tipo de proyecto, el presupuesto y el estado sin costo, y desbloquean la ciudad, el código postal, los contactos y los planos usando un lead incluido en su membresía.` },
          { q: '¿Cuánto cuesta un lead de construcción?', a: `Cada lead cuesta lo mismo: ${LEAD}. El oficio, la ciudad o el presupuesto no cambian el precio. Las membresías incluyen entre ${PLAN_INCLUDED_LEADS.basic} y ${PLAN_INCLUDED_LEADS.premium} leads al mes.` },
          { q: '¿Por qué no veo la ciudad del proyecto?', a: 'La ciudad, el código postal, la dirección y los datos de contacto permanecen bloqueados hasta que un subcontratista con membresía activa reclama el lead. Así se protege la información del propietario y el valor del lead.' },
          { q: '¿Publicar un proyecto tiene costo?', a: 'No. Publicar es gratis para propietarios y contratistas generales. Solo los subcontratistas pagan por desbloquear leads.' },
          { q: '¿GIGGONE está disponible en español?', a: 'Sí. Todo el marketplace funciona en inglés y español, incluyendo publicaciones, panel y soporte.' },
        ]
      : [
          { q: 'How does GIGGONE work?', a: `Homeowners and general contractors post real projects. Subcontractors browse the trade, project type, budget range and state for free, then unlock the city, ZIP, contact details and plan documents using an included lead from their membership.` },
          { q: 'How much do construction leads cost?', a: `Every lead costs the same flat ${LEAD}. Trade, city and budget never change the price. Memberships include ${PLAN_INCLUDED_LEADS.basic} to ${PLAN_INCLUDED_LEADS.premium} leads per month.` },
          { q: 'Why is the project city hidden?', a: 'City, ZIP, address and contact details stay locked until a subcontractor with an active membership claims the lead. That protects the poster\u2019s privacy and keeps the lead valuable for the sub who buys it.' },
          { q: 'Does it cost anything to post a project?', a: 'No. Posting is free for homeowners and general contractors. Only subcontractors pay to unlock leads.' },
          { q: 'Is GIGGONE available in Spanish?', a: 'Yes. The entire marketplace runs in English and Spanish, including posting, the dashboard and support.' },
        ]
  );

export const tradeFaqs = (tradeEn: string, tradeEs: string, lang: Lang, place?: string): Faq[] => {
  const where = place ? (lang === 'es' ? ` en ${place}` : ` in ${place}`) : lang === 'es' ? ' en California' : ' in California';
  const trade = lang === 'es' ? tradeEs : tradeEn;
  return fx(
    lang === 'es'
      ? [
          { q: `¿Cómo encuentran trabajo los subcontratistas de ${trade.toLowerCase()}${where}?`, a: `Los subcontratistas de ${trade.toLowerCase()} encuentran trabajo${where} revisando proyectos publicados por propietarios y contratistas generales en GIGGONE, y desbloqueando los que están dentro de su área de servicio con un lead incluido en su membresía.` },
          { q: `¿Cuánto cuesta un lead de ${trade.toLowerCase()}?`, a: `Un lead de ${trade.toLowerCase()} cuesta ${LEAD}, el mismo precio fijo que cualquier otro lead del marketplace.` },
          { q: `¿Necesito licencia para tomar trabajos de ${trade.toLowerCase()}${where}?`, a: `California exige una licencia CSLB para cualquier trabajo de construcción de $500 o más. Los contratistas generales verifican licencia y seguro antes de contratar.` },
          { q: `¿Qué información incluye un proyecto de ${trade.toLowerCase()}?`, a: `Cada publicación incluye el alcance, el tipo de proyecto, el rango de presupuesto, la fecha estimada y el estado. Al desbloquearlo aparecen la ciudad, el código postal, los contactos aprobados y los documentos adjuntos.` },
        ]
      : [
          { q: `How do ${trade.toLowerCase()} subcontractors find work${where}?`, a: `${trade} subcontractors find work${where} by browsing projects posted by homeowners and general contractors on GIGGONE, then unlocking the ones inside their service area with an included lead from their membership.` },
          { q: `How much does a ${trade.toLowerCase()} lead cost?`, a: `A ${trade.toLowerCase()} lead costs ${LEAD} — the same flat price as every other lead on the marketplace.` },
          { q: `Do I need a license to take ${trade.toLowerCase()} jobs${where}?`, a: `California requires a CSLB license for construction work valued at $500 or more. General contractors verify license class, status and insurance before awarding work.` },
          { q: `What information is included with a ${trade.toLowerCase()} project?`, a: `Every posting shows the scope, project type, budget range, target date and state. Unlocking reveals the city, ZIP, approved contact details and any attached plans or scope documents.` },
        ]
  );
};

export const locationFaqs = (place: string, kind: 'city' | 'county', lang: Lang): Faq[] => {
  const label = kind === 'county' ? `${place} County` : place;
  return fx(
    lang === 'es'
      ? [
          { q: `¿Qué proyectos de construcción hay en ${label}?`, a: `GIGGONE lista proyectos activos en ${label}, California — techado, concreto, electricidad, plomería, HVAC, remodelación y construcción comercial publicados por propietarios y contratistas generales.` },
          { q: `¿Cómo consigo trabajo de construcción en ${label}?`, a: `Cree una cuenta de subcontratista, seleccione sus oficios, revise los proyectos publicados en California y desbloquee los que estén en ${label} con un lead incluido en su membresía.` },
          { q: `¿Los contratistas generales de ${label} contratan subcontratistas por GIGGONE?`, a: `Sí. Los contratistas generales publican paquetes de trabajo con alcance, presupuesto y planos, y los subcontratistas verificados los desbloquean para cotizar.` },
          { q: `¿Publicar un proyecto en ${label} cuesta algo?`, a: `No. Publicar es gratis en toda California. Solo los subcontratistas pagan por desbloquear leads.` },
        ]
      : [
          { q: `What construction projects are available in ${label}?`, a: `GIGGONE lists active projects across ${label}, California — roofing, concrete, electrical, plumbing, HVAC, remodeling and commercial construction posted by homeowners and general contractors.` },
          { q: `How do I get construction work in ${label}?`, a: `Create a subcontractor account, select your trades, browse posted California projects and unlock the ones in ${label} using an included lead from your membership.` },
          { q: `Do general contractors in ${label} hire subs through GIGGONE?`, a: `Yes. General contractors post work packages with scope, budget range and plan documents, and verified subcontractors unlock them to bid.` },
          { q: `Does it cost anything to post a project in ${label}?`, a: `No. Posting is free everywhere in California. Only subcontractors pay to unlock leads.` },
        ]
  );
};

export const moneyFaqs = (trade: string, place: string, lang: Lang): Faq[] =>
  fx(
    lang === 'es'
      ? [
          { q: `¿Cuánto cuestan los leads de ${trade.toLowerCase()} en ${place}?`, a: `${LEAD} por lead — precio fijo. Las membresías incluyen leads mensuales, así que el costo por lead baja según el plan.` },
          { q: `¿Los leads de ${trade.toLowerCase()} en ${place} son exclusivos?`, a: `GIGGONE limita cuántos subcontratistas pueden desbloquear el mismo proyecto y muestra cuántos ya lo reclamaron antes de que gaste un lead.` },
          { q: `¿Qué tan rápido debo contactar un lead?`, a: `Dentro de la primera hora. La velocidad de respuesta es el factor que más influye en ganar el trabajo.` },
          { q: `¿Puedo cancelar mi membresía?`, a: `Sí. Los planes mensuales se cancelan cuando quiera; el plan Premium tiene un acuerdo de 12 meses.` },
        ]
      : [
          { q: `How much do ${trade.toLowerCase()} leads cost in ${place}?`, a: `${LEAD} per lead — one flat price. Memberships include monthly leads, so the effective cost per lead drops with the plan you choose.` },
          { q: `Are ${trade.toLowerCase()} leads in ${place} exclusive?`, a: `GIGGONE caps how many subcontractors can unlock the same project and shows you how many have already claimed it before you spend an included lead.` },
          { q: `How fast should I contact a new lead?`, a: `Within the first hour. Response speed is the single biggest factor in whether a subcontractor wins the job.` },
          { q: `Can I cancel my membership?`, a: `Yes. Monthly plans can be cancelled at any time; the Premium plan carries a 12-month agreement.` },
        ]
  );
